import React, { useContext, useEffect, useState } from "react";
import { StyledTransactionsTable, ToggleContainer } from "../../Wallets/components/TransactionsTable/style";
import TableTools from "../../../components/common/TableTools";
import ToggleButton from "../../../components/common/ToggleButton/ToggleButton";
import CustomTable from "../../../components/common/CustomTable";
import EmptyVirtualAccount from "../../Customers/components/EmptyVirtualAccount";
import SettlementMobileCard from "./SettlementMobileCard";
import PayoutMobilecard from "./PayoutMobilecard";
import DrawerContainer from "../../../components/common/Drawer/Drawer";
import PayoutTransactionDetails from "./PayoutTransactionDetails";
import dayjs from "dayjs";
import { vaCurrencySelectOptions } from "../../Wallets/utils";
import { payoutColumns, tableColumns } from "../utils";
import { convertToSentenceCase, getFirstName } from "../../../utils/gtag";
import exportFromJSON from "export-from-json";
import { formatLongString } from "../../../utils/formatString";
import { formatCurrencyAmount } from "../../../utils/currency";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { useWindowSize } from "@reach/window-size";
import { useNavigate, useParams } from "react-router-dom";
import { allWallets } from "../../Wallets/redux/slice";
import { getSettelementsPayOutDetails, getSettlementsPayInDetails } from "../../../app/queries/settlements";
import Loader from "../../../components/common/Loader";

interface DateProps {
    startDate: Date;
    endDate: Date;
}

type TableProps = {
    transactions?: any;
    error?: any;
    isLoading?: boolean;
    currencyOfInterest?: string;
}

export default function SettlementTransactionsTable({ currencyOfInterest }: TableProps) {
    const { currencyCode } = useParams()

    const { onShowAlert } = useContext(NetworkErrorAlertContext);
    const { wallets } = useAppSelector(
        (state) => state?.wallets,
    );
    const { width } = useWindowSize();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const toggleType = ["Settlements", "Payouts"];
    const dispatch = useAppDispatch();
    useState([]);
    const [settlementDetails, setSettlementDetails] =
        useState(null);
    const [date, setDate] = useState<DateProps>();
    const [search, setSearch] = useState("");
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [currentPayInCurrencyForFilter, setCurrentPayInCurrencyForFilter] = useState<string | null>(null)
    const [appliedPayInFilters, setAppliedPayInFilters] = useState({
        startDate: null,
        endDate: null,
        currency: null,
    });

    const { businessId, userId } = useAppSelector(
        (state) => state?.users,
    );

    const {
        business,
    } = useAppSelector((state) => state.settings);

    const balance = wallets?.find(
        (curr) => curr.currency === (currencyOfInterest ? currencyOfInterest : "NGN"),
    );

    const showDrawer = () => {
        setOpen(true);
    };

    const searchParams = new URLSearchParams(
        location.search,
    );
    const activeParam = searchParams.get("");
    const initialActiveToggle =
        activeParam && toggleType.includes(activeParam)
            ? activeParam
            : "Settlements";

    const [activeToggle, setActiveToggle] = useState(
        initialActiveToggle,
    );

    const isPayouts = activeToggle === "Payouts";

    useEffect(() => {
        if (businessId && userId) {
            dispatch(allWallets({ businessId }));
        }
    }, [businessId, userId]);

    useEffect(() => {
        setActiveToggle(initialActiveToggle);
    }, [initialActiveToggle]);

    const getSettlementsPayOutPayload = {
        data: {
            walletId: balance?.id,
            product: "VirtualAccount"
        },
        activeToggle,
        businessId
    }

    const { data: allSettlementPayOut, error: payOutError, isLoading: payOutLoading } = getSettelementsPayOutDetails(getSettlementsPayOutPayload)

    const formattedPayOutTableData =
        allSettlementPayOut?.foreignBankTransferRequests?.map((data) => {
            const {
                request_id: key,
                requestId: transaction_id,
                destinationCurrency,
                amount,
                sourceCurrency,
                fee,
                accountHolderName,
                bankName,
                createdAt
            } = data;

            const formattedTransactionId = formatLongString(transaction_id, 15);
            const formattedAmountPaid = formatCurrencyAmount(destinationCurrency, amount);
            const formattedFees = formatCurrencyAmount(sourceCurrency, fee);
            const payoutCurrency = sourceCurrency;
            const formattedRate = Number(data?.rate || 1)?.toFixed(data?.destinationCurrency === 'NGN' ? 2 : 6);

            let totalDebit;
            if (data?.rate > 1) {
                const calculatedTotal = amount * data?.rate + fee;
                totalDebit = formatCurrencyAmount(sourceCurrency, calculatedTotal);
            } else {
                const totalAmountWithFee = amount + fee;
                totalDebit = formatCurrencyAmount(sourceCurrency, totalAmountWithFee);
            }

            const beneficiary = getFirstName(accountHolderName || business?.name || 'N/A');
            const date = dayjs(createdAt).format("DD.MM.YYYY - HH:mm");
            const accountName = accountHolderName || business?.name || "N/A";
            const accountNumber = data.accountNumber || "N/A";
            const bankNameFormatted = bankName || "N/A";
            const transactionRef = data.requestId || "N/A";

            return {
                key,
                transaction_id: formattedTransactionId,
                request: transaction_id,
                amount_paid: formattedAmountPaid,
                currency: sourceCurrency,
                fees: formattedFees,
                payoutCurrency,
                rate: formattedRate,
                total_debit: totalDebit,
                beneficiary,
                date,
                account_name: accountName,
                account_number: accountNumber,
                bank_name: bankNameFormatted,
                transactionRef
            };
        })

    const handleReset = async () => {
        setDate(null);
        setCurrentPayInCurrencyForFilter(null);
        setAppliedPayInFilters({ startDate: null, endDate: null, currency: null });
    };

    const onPageChange = (page: number) => {
        setPageNumber(page);
    };

    const getSettlementsPayInPayload = {
        data: {
            startDay: appliedPayInFilters.startDate || null,
            endDay: appliedPayInFilters.endDate || null,
            currency: appliedPayInFilters.currency || currencyOfInterest || null,
            pageNumber: pageNumber,
            pageSize: 10,
            virtualAccountSettlementStatus: 0,
            businessId,
        },
        activeToggle,
    }

    const { pageSize } = getSettlementsPayInPayload.data;

    const handlePayInCurrencyFilterChange = (value) => {
        setCurrentPayInCurrencyForFilter(value);
    }

    const handlePayInFilterProcess = () => {
        setAppliedPayInFilters({
            ...appliedPayInFilters,
            currency: currentPayInCurrencyForFilter || currencyOfInterest || null,
            startDate: date?.[0] ? dayjs(date[0]).format("YYYY-MM-DD") : null,
            endDate: date?.[1] ? dayjs(date[1]).format("YYYY-MM-DD") : null,
        })
        onPageChange(1)
    }

    const { data: allSettlementPayIn, error: payInError, isLoading: payInLoading } = getSettlementsPayInDetails(getSettlementsPayInPayload)

    const settlementsPayInTableData =
        allSettlementPayIn?.virtualAccountCollectionSettlements
            ?.filter(
                (data) =>
                    data.virtualAccountSettlementStatus ===
                    "SETTLEMENT",
            )
            .map((data) => ({
                key: data.id,
                transaction_id: formatLongString(
                    data.reference,
                    20,
                ),
                source: "Virtual account",
                transaction_type:
                    convertToSentenceCase(
                        data.virtualAccountSettlementStatus,
                    ),
                currency: data.currency,
                amount: formatCurrencyAmount(
                    data.currency,
                    data.amount,
                ),
                date: dayjs(data.createdAt).format(
                    "DD.MM.YYYY - HH:mm",
                ),
            }));

    const filteredPayInTransactions = settlementsPayInTableData?.filter((item) =>
        Object.values(item).some(
            (val) =>
                typeof val === "string" &&
                val.toLowerCase().includes(search.toLowerCase())
        )
    );

    const getExportData = async () => {
        return allSettlementPayIn?.virtualAccountCollectionSettlements?.map((data) => ({

            "Transaction Id":
                data.reference,
            "Source": "Virtual account",
            "Transaction type":
                convertToSentenceCase(
                    data.virtualAccountSettlementStatus,
                ),
            "Currency": data.currency,
            "Amount": data?.amount + " " + data?.currency,
            "Date": dayjs(data.createdAt).format(
                "DD.MM.YYYY - HH:mm",
            ),
        }));
    };

    const exportToXLS = async () => {
        const data = await getExportData();
        const exportType = exportFromJSON.types.xls;
        exportFromJSON({
            data,
            fileName: "Settlement transactions",
            exportType,
        });
    };

    const exportToCSV = async () => {
        const data = await getExportData();
        const exportType = exportFromJSON.types.csv;
        exportFromJSON({
            data,
            fileName: "Settlement transactions",
            exportType,
        });
    };

    const handleRowClick = (row: any) => {
        setSettlementDetails(row);
        showDrawer();
    };

    const onClose = () => {
        setOpen(false);
    };

    const handleToggle = (item) => {
        setActiveToggle(item);
        navigate(currencyOfInterest ? `/settlements/${currencyCode}?=${item}` : `/settlements?=${item}`);
    };

    const error = payInError || payOutError
    {
        error && onShowAlert("An error occurred", error as string);
    }
    
    return (
        <>
            <StyledTransactionsTable>
                <h3>Transactions</h3>

                <div>
                    {!isPayouts && (
                        <TableTools
                            placeHolder="Search"
                            onSearchChange={(e) =>
                                setSearch(
                                    e.target.value,
                                )
                            }
                            onFilterButtonClick={handlePayInFilterProcess}
                            onResetButtonClick={
                                handleReset
                            }
                            onDateChange={(date) =>
                                setDate(date)
                            }
                            onXLSButtonClick={
                                exportToXLS
                            }
                            onCSVButtonClick={
                                exportToCSV
                            }
                            showSelectOptions={!currencyCode}
                            selectOptions={vaCurrencySelectOptions}
                            selectTitle="Currency"
                            onStatusChange={handlePayInCurrencyFilterChange}
                        />
                    )}
                </div>
                <ToggleContainer>
                    <ToggleButton
                        toggleType={toggleType}
                        activeToggle={activeToggle}
                        setActiveToggle={setActiveToggle}
                        handleToggle={handleToggle}
                    />
                </ToggleContainer>
                {!isPayouts && (
                    <>
                        {width > 767 ? (
                            <CustomTable
                                columns={tableColumns}
                                dataSource={filteredPayInTransactions}
                                handleRowClick={handleRowClick}
                                currentPage={pageNumber}
                                pageSize={pageSize}
                                totalItemCount={
                                    allSettlementPayIn?.totalCount
                                }
                                onPageChange={onPageChange}
                                extraEmptyChildren={
                                    <EmptyVirtualAccount
                                        title="No transactions yet"
                                        paragraph="All received or sent transactions will appear here."
                                    />
                                }
                            />
                        ) : (
                            <>
                                {settlementsPayInTableData?.map((data) => (
                                    <SettlementMobileCard
                                        key={data.key}
                                        transactionRef={formatLongString(
                                            data.transaction_id,
                                            10,
                                        )}
                                        source="Virtual account"
                                        transactionType={
                                            data.transaction_type
                                        }
                                        currency={
                                            data.currency
                                        }
                                        amount={data.amount}
                                        date={data.date}
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}

                {isPayouts && (
                    <>
                        {width > 767 ? (
                            <CustomTable
                                columns={payoutColumns}
                                dataSource={formattedPayOutTableData}
                                currentPage={pageNumber}
                                handleRowClick={
                                    handleRowClick
                                }
                                totalItemCount={
                                    allSettlementPayOut?.totalElements
                                }
                                onPageChange={onPageChange}
                                extraEmptyChildren={
                                    <EmptyVirtualAccount
                                        title="No transactions yet"
                                        paragraph="All received or sent transactions will appear here."
                                    />
                                }
                            />
                        ) : (
                            <>
                                {formattedPayOutTableData?.map((data) => (
                                    <PayoutMobilecard
                                        key={data.id}
                                        transactionRef={formatLongString(
                                            data.transaction_id,
                                            10,
                                        )}
                                        payoutAmount={
                                            data.amount_paid
                                        }
                                        transferFees={
                                            data.fees
                                        }
                                        rate={data.rate}
                                        totalDebit={
                                            data.total_debit
                                        }
                                        beneficiary={
                                            data.beneficiary
                                        }
                                        date={data.date}
                                        payoutCurrency={
                                            data.currency
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}
            </StyledTransactionsTable>

            <DrawerContainer
                title={isPayouts ? "Payout details" : "Settlement details"}
                onClose={onClose}
                open={open}
                placement={"right"}
            >
                <PayoutTransactionDetails
                    heading="Details"
                    isPayouts={isPayouts}
                    transactionId={
                        settlementDetails?.transaction_id
                    }
                    request={settlementDetails?.request}
                    transactionAmount={
                        isPayouts ? settlementDetails?.amount_paid : settlementDetails?.amount
                    }
                    currency={isPayouts ? settlementDetails?.payoutCurrency : settlementDetails?.currency}
                    transferFee={settlementDetails?.fees || 'N/A'}
                    rate={settlementDetails?.rate || 'N/A'}
                    totalDebit={
                        settlementDetails?.total_debit
                    }
                    date={settlementDetails?.date}
                />
                <br />

                {isPayouts && <PayoutTransactionDetails
                    heading="Beneficiary"
                    bankName={settlementDetails?.bank_name}
                    accountName={
                        settlementDetails?.account_name
                    }
                    accountNumber={
                        settlementDetails?.account_number
                    }
                    isBeneficiary
                />}
            </DrawerContainer>
            <Loader isLoading={payInLoading || payOutLoading} />
        </>
    )
}